/**
 * Process Analyzer Module
 * Transforms API process graph data into format expected by the graph visualizer
 */
export class ProcessAnalyzer {
    constructor() {
        // Store the process start and end identifiers from API
        this.processStartId = null;
        this.processEndId = null;
        this.totalCases = 1; // avoid division by zero
    }

    /**
     * Analyze API process graph data and transform it for visualization
     * @param {Object} apiData - Process graph data from API
     * @returns {Object} - Process flow information and metrics in expected format
     */
    analyze(apiData) {
        if (!apiData || typeof apiData !== 'object') {
            throw new Error('No API data provided');
        }

        if (!apiData.edges || !apiData.nodes) {
            throw new Error('Invalid API data: missing edges or nodes');
        }

        // Store process start/end identifiers
        this.processStartId = apiData.process_start;
        this.processEndId = apiData.process_end;

        // Transform API data to expected format
        const activities = this.transformNodes(apiData.nodes);
        const edges = this.transformEdges(apiData.edges);
        const timingMetrics = this.extractTimingMetrics(apiData.edges);
        const processStartActivities = this.getProcessStartActivities(apiData.edges);
        const processEndActivities = this.getProcessEndActivities(apiData.edges);
        const totalCases = apiData.total_cases;
        this.totalCases = totalCases; 
        console.log("BEGIN MOST COMMON PATH")
        const mostCommonPath = this.calculateMostCommonPath(apiData.edges);
        console.log("MOST COMMON PATH:", mostCommonPath)

        return {
            activities,
            edges,
            // activityCounts: activityCounts,
            // activityPercentiles,
            caseVariants: [], // Not available from API data
            timingMetrics,
            processStartActivities,
            processEndActivities,
            totalCases,
            mostCommonPath
        };
    }


    // gets dict of dicts of all activities
    // returns dict of dicts of all actictities
    //   node_id remains the key
    //  adds:
    //    - frequency of activity, i.e. case_count / case => percentage of cases present
    //    - percentile of activity, i.e. rank in all activities by count of case presence
    transformNodes(apiNodes) {
        if (!apiNodes || typeof apiNodes !== 'object') {
            throw new Error('Invalid nodes data');
        }

        // Transform into list of dictionary
        const transformed_nodes = Object.entries(apiNodes).map(([nodeId, nodeData]) => ({
            id: nodeId,
            label: nodeData.label || nodeId,
            count: nodeData.case_count || 0, // #MAPPING_CASE_COUNT_TO_COUNT
        }));

        const sortedNodes = transformed_nodes.sort((a, b) => b.count - a.count);
        const totalNodes = sortedNodes.length;
        // const sum_of_counts = sortedNodes.reduce((sum, n) => sum + n.count, 0);


        // Return as dict of dicts with node.id as key
        return sortedNodes.reduce((acc, node, index) => {
            const percentile = Math.ceil((index / totalNodes) * 100);
            const frequency = Math.ceil((node.count / this.totalCases) * 100);

            acc[node.id] = {
                label: node.label,
                count: node.count,
                frequency: frequency,
                percentile: percentile
            };

            return acc;
        }, {});
    }


    /**
     * Transform API edges to expected format with percentiles
     * @param {Array} apiEdges - Edges array from API
     * @returns {Array} - Transformed edges with percentiles
     */
    transformEdges(apiEdges) {
        // Sort edges by case_count (highest first) for percentile calculation
        const sortedEdges = [...apiEdges].sort((a, b) => b.case_count - a.case_count); // #MAPPING_CASE_COUNT_TO_COUNT
        const totalEdges = sortedEdges.length;

        return sortedEdges.map((edge, index) => {
            // Calculate percentile (0-100)
            const percentile = Math.ceil((index / totalEdges) * 100);

            // percentage against the maximum edge count
            // const max_percentage = totalEdges > 0 ? Math.ceil((edge.count / sortedEdges[0].count) * 100) : 0;

            // percentage count against sum of all counts
            // const sum_of_counts = sortedEdges.reduce((sum, e) => sum + e.count, 0);
            const frequency = Math.ceil((edge.case_count / this.totalCases) * 100); // #MAPPING_CASE_COUNT_TO_COUNT
            
            return {
                source: edge.source,
                target: edge.target,
                count: edge.count,
                percentile: percentile, // at filter >= percentile this is would be shown
                //max_percentage: max_percentage,
                frequency: frequency // at filter <= frequency this is would be shown
            };
        });
    }

    /**
     * Extract timing metrics from edges
     * @param {Array} apiEdges - Edges array from API
     * @returns {Object} - Timing metrics object
     */
    extractTimingMetrics(apiEdges) {
        const timingMetrics = {};

        apiEdges.forEach(edge => {
            if (!timingMetrics[edge.source]) {
                timingMetrics[edge.source] = {};
            }

            // Convert avg_duration from API (assuming it's in seconds) to milliseconds and hours
            const avgDurationMs = edge.avg_duration * 1000; // Convert to milliseconds
            const avgDurationHours = edge.avg_duration / 3600; // Convert to hours

            timingMetrics[edge.source][edge.target] = {
                avgDurationMs: avgDurationMs,
                avgDurationHours: avgDurationHours
            };
        });

        return timingMetrics;
    }

    /**
     * Get process start activities from edges
     * @param {Array} apiEdges - Edges array from API
     * @returns {Array} - Array of start activities with count
     */
    getProcessStartActivities(apiEdges) {
        const startActivities = new Map();

        // Find all edges from process start
        apiEdges.forEach(edge => {
            if (edge.source === this.processStartId) {
                startActivities.set(edge.target, edge.case_count); // #MAPPING_CASE_COUNT_TO_COUNT
            }
        });

        return Array.from(startActivities.entries())
            .map(([activity, count]) => ({ activity, count }))
            .sort((a, b) => b.count - a.count);
    }

    /**
     * Get process end activities from edges
     * @param {Array} apiEdges - Edges array from API
     * @returns {Array} - Array of end activities with count
     */
    getProcessEndActivities(apiEdges) {
        const endActivities = new Map();

        // Find all edges to process end
        apiEdges.forEach(edge => {
            if (edge.target === this.processEndId) {
                endActivities.set(edge.source, edge.case_count); // #MAPPING_CASE_COUNT_TO_COUNT
            }
        });

        return Array.from(endActivities.entries())
            .map(([activity, count]) => ({ activity, count }))
            .sort((a, b) => b.count - a.count);
    }

    calculateMostCommonPath(apiEdges) {
        // Build adjacency map for easier traversal
        const adjacencyMap = new Map();
        apiEdges.forEach(edge => {
            if (!adjacencyMap.has(edge.source)) {
            adjacencyMap.set(edge.source, []);
            }
            adjacencyMap.get(edge.source).push({
            target: edge.target,
            count: edge.case_count // #MAPPING_CASE_COUNT_TO_COUNT
            });
        });

        // Sort outgoing edges by count for each node
        adjacencyMap.forEach((edges) => {
            edges.sort((a, b) => b.count - a.count);
        });

        // Precompute a soft upper bound on path length to avoid infinite loops
        const nodeSet = new Set();
        apiEdges.forEach(e => { nodeSet.add(e.source); nodeSet.add(e.target); });
        const maxSteps = nodeSet.size + 5; // small guard margin

        // Start with Process Start
        const path = [this.processStartId];
        const visited = new Set([this.processStartId]);
        let currentNode = this.processStartId;
        let steps = 0;

        // Continue until we reach Process End
        while (currentNode !== this.processEndId) {
            if (++steps > maxSteps) {
            // Safety guard: force end
            path.push(this.processEndId);
            break;
            }

            const outgoingEdges = adjacencyMap.get(currentNode) || [];

            if (outgoingEdges.length === 0) {
            // No outgoing edges, add Process End and break
            path.push(this.processEndId);
            break;
            }

            // Find direct path to end
            const endEdge = outgoingEdges.find(edge => edge.target === this.processEndId);

            // Prefer end if it's the most common or we have a long path
            if (endEdge && (
            endEdge === outgoingEdges[0] ||
            (path.length >= 3 && endEdge.count >= outgoingEdges[0].count * 0.8)
            )) {
            currentNode = this.processEndId;
            } else {
            // Prefer the most common *unvisited* next node and not the process start
            const candidates = outgoingEdges.filter(e =>
                e.target !== this.processStartId && !visited.has(e.target)
            );

            if (candidates.length > 0) {
                currentNode = candidates[0].target;
            } else {
                // If all options loop back, terminate at end to avoid cycles
                currentNode = this.processEndId;
            }
            }

            path.push(currentNode);
            visited.add(currentNode);
        }

        console.log("Calculated most common path:", path);
        return path;
    }
}

// Export singleton instance
export const processAnalyzer = new ProcessAnalyzer();